<script>
	// NPM Modules
	import Link from 'svelte-navaid/Link.svelte';
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	import advancedFormat from 'dayjs/plugin/advancedFormat';
	import utc from 'dayjs/plugin/utc';
	import timezone from 'dayjs/plugin/timezone';
	import collect from 'collect.js';
	// Constants
	import { UserStageColours, UserRatingColours, UserSubRatingColours } from '@/Constants/User.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import Label from '@/Components/Label.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Stores
	import CampaignStore from '@/Stores/CampaignStore.js';
	// Services
	import ApiService from '@/Services/ApiService.js';
	import { paginationBuilder, setUrlParameter } from '@/Services/Helpers.js';

	dayjs.extend(localizedFormat);
	dayjs.extend(advancedFormat);
	dayjs.extend(utc);
	dayjs.extend(timezone);

	export let campaignId;
	export let open = false;

	let loading = true;

	let campaign = $CampaignStore.firstWhere('id', campaignId);

	let users = collect();
	let accountManagers = collect();
	let numberOfPages = 0;
	let numberPerPage = 50;
	let page = 1;
	let pagination;
	let listGeneratedAt;

	$: links = users.count() && paginationBuilder(pagination);
	$: numberOfPages = users.count() && pagination && pagination.total_pages;

	function changePage(e) {
		if (e) {
			page = e.detail.page;
			getCampaignUsers();
			setUrlParameter('page', page);
		}
	}

	async function getCampaignUsers(reload = false) {
		loading = true;
		CampaignStore.getUserForCampaign(campaignId, { perPage: numberPerPage, page }, reload)
			.then((userList) => {
				users = userList.data;
				accountManagers = userList.meta?.account_managers;
				pagination = userList.meta && userList.meta.pagination;
				listGeneratedAt = userList.meta && userList.meta?.user_list_generated_at;
			})
			.catch()
			.finally(() => (loading = false));
	}

	const statCols = {
		'User ID': '',
		'User PIN': '',
		Username: '',
		'First Name': '',
		'Last Name': '',
		State: '',
		Country: '',
		Status: '',
		Rating: '',
		'Sub-Rating': '',
		Tier: '',
		'User Stage': '',
		'Account Manager': ''
	};

	async function reloadUserList() {
		await getCampaignUsers(true);
	}
	async function downloadUserList() {
		ApiService.downloadFile(
			`${campaign.resourceUrl()}${campaign.id}/users/export`,
			`${campaign.campaign_id} ${campaign.name} users - ${dayjs().format('YYYYMMDDHHmmss')}`
		);
	}

	getCampaignUsers();
</script>

{#if open && campaign}
	<Modal on:close={() => (open = false)} className="md:w-4/5 lg:w-3/4 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			Campaign: {campaign.campaign_id} - {campaign.name}
		</h2>
		<div class="px-4 pb-4">
			<hr class="my-2" />
			<div class="flex flex-row w-full">
				<div class="mt-2 overflow-auto max-report-height bg-white w-full">
					<div class="flex flex-row justify-end items-center space-x-4">
						{#if listGeneratedAt}
							<div class="flex flex-col justify-center items-center">
								<span class="text-xs">List generated at:</span>
								<span class="text-xs">{listGeneratedAt ? dayjs(listGeneratedAt).format('D MMM YYYY, HH:mm') : ''}</span>
							</div>
						{/if}
						<LoadingButton
							{loading}
							class="focus:outline-none btn-primary-action p-2 rounded m-1.5 flex flex-row justify-center items-center"
							on:click={reloadUserList}
						>
							<Icon name="unfinalise" classes="w-4 h-4 mr-2"></Icon>
							Reload user list
						</LoadingButton>
						<LoadingButton
							disabled={!(users && users.count() > 0) || loading}
							class="focus:outline-none btn-primary p-2 rounded m-1.5 flex flex-row justify-center items-center"
							on:click={downloadUserList}
						>
							<Icon name="download" classes="w-4 h-4 mr-2"></Icon>
							Export as CSV
						</LoadingButton>
					</div>
					{#if loading}
						<div class="flex items-center justify-center h-full w-full">
							<div class="lds-ring">
								<div />
								<div />
								<div />
								<div />
							</div>
						</div>
					{:else if users && users.count() > 0}
						{#if numberOfPages > 1}
							<Pagination {links} on:click={changePage} className="my-3" />
						{/if}
						<div class="table w-full">
							<div class="table-header-group">
								<div class="table-row text-sm text-center bg-grey text-light">
									{#each Object.entries(statCols) as col, i}
										<div class:text-left={i < 1} class:pl-2={i < 1} class="sticky top-0 table-cell py-2 pl-1 bg-grey">
											<div class="flex justify-center">
												{col[0]}
											</div>
										</div>
									{/each}
								</div>
							</div>
							<div class="table-row-group">
								{#each users.all() as user}
									<div class="table-row text-sm text-center hover:bg-gray-200">
										<div class="table-cell px-2 py-2 border-b border-l">
											<Link
												href={`/users/${user.id}`}
												class="hover:text-blue-700 text-blue-500 flex flex-row items-center"
											>
												{user.id}
											</Link>
										</div>
										<div class="table-cell px-4 py-2 border-b">
											<Link
												href={`/users/${user.id}`}
												class="hover:text-blue-700 text-blue-500 flex flex-row items-center"
											>
												{user.pin}
											</Link>
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{user.name}
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{user.first_name}
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{user.last_name}
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{user.state}
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{user.country}
										</div>
										<div class="table-cell px-4 py-2 border-b">
											<Label label={user.status?.name} classes="whitespace-nowrap" />
										</div>
										<div class="table-cell px-4 py-2 border-b">
											<div
												class="ml-2 focus:outline-none rounded px-2 py-1 text-xs text-center justify-self-end {UserRatingColours[
													user.rating.id
												]?.bg ?? 'bg-gray-300'} {UserRatingColours[user.rating.id]?.text ?? 'text-dark'}"
											>
												{user.rating.name}
											</div>
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{#if user.sub_rating?.id}
												<div
													class="ml-2 focus:outline-none rounded px-2 py-1 text-xs text-center justify-self-end {UserSubRatingColours[
														user.sub_rating?.id
													].bg} {UserSubRatingColours[user.sub_rating?.id].text}"
												>
													{user.sub_rating?.name}
												</div>
											{:else}
												-
											{/if}
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{#if user.tier?.id}
												<Label label={user.tier?.name} classes="whitespace-nowrap" />
											{:else}
												-
											{/if}
										</div>
										<div class="table-cell px-4 py-2 border-b">
											{#if user.stage?.id}
												<div
													class="ml-2 focus:outline-none rounded px-2 py-1 text-xs text-center justify-self-end {UserStageColours[
														user.stage?.id
													].bg} {UserStageColours[user.stage?.id].text}"
												>
													{user.stage?.name}
												</div>
											{:else}
												-
											{/if}
										</div>
										<div class="table-cell px-4 py-2 border-b border-r">
											{#if user.account_manager_id}
												<div
													class="ml-2 focus:outline-none rounded px-2 py-1 text-white text-xs text-center justify-self-end bg-purple-500 whitespace-nowrap"
													title="Account Manager"
												>
													{user.account_manager_id && user.account_manager_id in accountManagers
														? accountManagers[user.account_manager_id]
														: ''}
												</div>
											{:else}
												-
											{/if}
										</div>
									</div>
								{/each}
							</div>
						</div>
						{#if numberOfPages > 1}
							<Pagination {links} on:click={changePage} className="my-3" />
						{/if}
					{:else}
						<div class="w-full py-10 text-center">No users selected for this campaign</div>
					{/if}
				</div>
			</div>
		</div>
	</Modal>
{/if}

<style>
	.max-report-height {
		height: calc(var(--vh, 1vh) * 100 - 20rem);
	}
</style>
